<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Swiper from 'swiper'
import { Autoplay, Pagination } from 'swiper/modules'
import { chapterConfig as rocnovelConfig } from '../configs/rocnovel'
import { chapterConfig as ocereadConfig } from '../configs/oceread'
import { chapterConfig as ficmoonConfig } from '../configs/ficmoon'
import PaymentMethodSelector from './PaymentMethodSelector.vue'
import SubscribeItemContent from './SubscribeItemContent.vue'
import { useUserStore } from '@/stores'
import 'swiper/css'
import 'swiper/css/pagination'

import { usePurchase } from '@/composables/usePurchase'
import { useSubscript } from '@/composables/useSubscript'
import { colors } from '@/config/brands'
import { loadBrandConfig } from '@/utils/loadBrandConfig'
import { useI18n } from '@/composables/useI18n'
import { createShowPaymentMethodSelector } from '@/composables/usePaymentMethodDisplay'

const props = defineProps<{
  value: boolean
  contentId: string
  contentChapterId: string
  bookName: string
  chapterName: string
  serialStatus: number
}>()

const emit = defineEmits<{
  (e: 'input', value: boolean): void
  (e: 'success'): void
}>()

// 加载当前品牌的 chapter 配置
const config = loadBrandConfig({
  rocnovel: rocnovelConfig,
  oceread: ocereadConfig,
  ficmoon: ficmoonConfig,
})

const { t } = useI18n()
const userStore = useUserStore()

// 使用充值 composable（支持多支付方式）
const {
  templateProducts,
  PayTemplateSuccess,
  PayTemplate,
  currentPaymentMethod: purchasePaymentMethod,
  availablePaymentMethods: purchaseAvailableMethods,
  selectPaymentMethod: selectPurchasePaymentMethod,
  productLists: purchaseProductLists,
} = usePurchase('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
  bookName: props.bookName,
  chapterName: props.chapterName,
  serialStatus: props.serialStatus,
})

// 使用订阅 composable（支持多支付方式）
const {
  loaded,
  subscribeSuccess,
  getUnitPrice,
  onSubscribeProducts,
  getIsImageBackground,
  isZero,
  availablePaymentMethods: subscriptAvailableMethods,
  selectPaymentMethod: selectSubscriptPaymentMethod,
  currentPaymentMethod: subscriptPaymentMethod,
  hasPayPalPasswordlessPayment,
} = useSubscript('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
})

const active = ref(0)
const swiperInstance = ref<Swiper | null>(null)
const swiperInitTimer = ref<NodeJS.Timeout | null>(null) // 防抖定时器

// 统一的支付方式状态
const currentPaymentMethod = computed({
  get: () => {
    // 如果 usePurchase 的 productLists 为空，使用 useSubscript 的当前支付方式
    if (!purchaseProductLists.value || purchaseProductLists.value.length === 0)
      return subscriptPaymentMethod.value

    return purchasePaymentMethod.value
  },
  set: (val) => {
    selectPurchasePaymentMethod(val)
    selectSubscriptPaymentMethod(val)
  },
})

// 统一的可用支付方式
const availablePaymentMethods = computed(() => {
  // 若开启 PayPal passwordless，则仅保留 PayPal 支付方式
  if (hasPayPalPasswordlessPayment.value) {
    const all = [...(purchaseAvailableMethods.value || []), ...(subscriptAvailableMethods.value || [])]
    const mergedUnique = all.filter((m, i, arr) =>
      arr.findIndex(x => String(x.paymentMethod).toLowerCase() === String(m.paymentMethod).toLowerCase()) === i,
    )
    return mergedUnique.filter(m => String(m.paymentMethod).toLowerCase() === 'paypal')
  }

  // 使用充值或订阅的可用支付方式，确保返回有效数组
  const purchaseMethods = purchaseAvailableMethods.value || []
  const subscriptMethods = subscriptAvailableMethods.value || []
  // 优先返回数量更多的那一侧
  if (purchaseMethods.length > subscriptMethods.length)
    return purchaseMethods

  if (subscriptMethods.length > purchaseMethods.length)
    return subscriptMethods

  // 数量相等的情况
  if (purchaseMethods.length === 1 && subscriptMethods.length === 1) {
    const p = String(purchaseMethods[0]?.paymentMethod ?? '')
    const s = String(subscriptMethods[0]?.paymentMethod ?? '')
    // 若 paymentMethod 相同，则按 purchaseMethods 展示
    if (p === s)
      return purchaseMethods

    // 不相同则合并并去重（按 paymentMethod 去重）
    const merged = [...purchaseMethods, ...subscriptMethods].filter(
      (m, i, arr) => arr.findIndex(x => String(x.paymentMethod) === String(m.paymentMethod)) === i,
    )
    return merged
  }

  // 其他相等（>1）的情况，默认展示 purchaseMethods
  return purchaseMethods
})

// 是否显示支付方式选择器
const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

const subscribes = computed(() => {
  // 从当前支付方式的商品列表中提取商品
  const currentMethod = Number(currentPaymentMethod.value)

  // 确保 subscriptAvailableMethods 是有效的数组
  if (!subscriptAvailableMethods.value || !Array.isArray(subscriptAvailableMethods.value))
    return []

  // 查找匹配当前支付方式的商品列表
  const currentMethodList = subscriptAvailableMethods.value.find(
    (list: any) => Number(list?.paymentMethod) === currentMethod,
  )

  // 如果没有找到对应的商品列表，返回空数组
  if (!currentMethodList?.products || !Array.isArray(currentMethodList.products))
    return []

  // 过滤出需要在弹窗中展示的商品（使用宽松的真值判断）
  return currentMethodList.products.filter((item: any) => item && item.purchasePopShow)
})

// 判断是否只有订阅商品（没有充值商品）
const hasOnlySubscribes = computed(() => {
  return (!templateProducts.value || templateProducts.value.length === 0) && subscribes.value?.length > 0
})

function handlePayTemplate(item: any, index: number) {
  active.value = index
  PayTemplate(item)
}

function initSwiper() {
  // 清除之前的定时器
  if (swiperInitTimer.value) {
    clearTimeout(swiperInitTimer.value)
    swiperInitTimer.value = null
  }

  // 先销毁已存在的 Swiper 实例
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true)
    swiperInstance.value = null
  }

  // 只有当有订阅商品时且没有只显示订阅商品（轮播模式）才创建 Swiper
  if (!subscribes.value?.length || hasOnlySubscribes.value)
    return

  // 使用防抖，延迟初始化以避免频繁切换时的问题
  // 使用 nextTick 确保 DOM 更新后再创建 Swiper
  nextTick(() => {
    // 再次检查，确保在 nextTick 后条件仍然满足
    if (!subscribes.value?.length || hasOnlySubscribes.value)
      return

    // 创建新的 Swiper 实例
    swiperInstance.value = new Swiper('.banner.swiper', {
      modules: [Pagination, Autoplay],
      spaceBetween: 0,
      autoplay: {
        delay: 3500,
        disableOnInteraction: false, // 用户交互后不停止自动播放
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      loop: subscribes.value.length > 1, // 只有多个项目时才开启循环
      observer: true, // 开启动态检查器
      observeParents: true, // 监听父元素
      observeSlideChildren: true, // 监听幻灯片子元素
      resizeObserver: true, // 监听容器尺寸变化
    })
  })
}

// 监听弹窗打开状态，重新初始化 Swiper
watch(
  () => props.value,
  (newVal) => {
    if (newVal && loaded.value && subscribes.value?.length && !hasOnlySubscribes.value) {
      // 弹窗打开时，初始化 Swiper
      initSwiper()
    }
  },
)

watch(
  () => loaded.value,
  (val) => {
    if (val && props.value && subscribes.value?.length && !hasOnlySubscribes.value)
      initSwiper()
  },
)

// 监听 subscribes 变化，重新初始化 Swiper
watch(
  () => subscribes.value,
  (newVal) => {
    // 当数据变化时，重新初始化 Swiper
    if (loaded.value && newVal?.length && props.value && !hasOnlySubscribes.value)
      initSwiper()
  },
)

// 监听支付方式切换，重新初始化 Swiper
watch(
  currentPaymentMethod,
  () => {
    if (loaded.value && subscribes.value?.length && props.value && !hasOnlySubscribes.value) {
      initSwiper()
    }
    else {
      if (swiperInstance.value) {
        swiperInstance.value.destroy(true, true)
        swiperInstance.value = null
      }
    }
  },
)

// 监听页面可见性变化
function handleVisibilityChange() {
  if (!document.hidden && props.value && loaded.value && subscribes.value?.length && !hasOnlySubscribes.value) {
    // 页面重新可见时，重新初始化 Swiper
    initSwiper()
  }
}

onMounted(() => {
  // 监听页面可见性变化
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onBeforeUnmount(() => {
  // 清理监听器
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  // 销毁 Swiper 实例
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true)
    swiperInstance.value = null
  }
})

watch(
  () => subscribeSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)

watch(
  () => PayTemplateSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)

// 定义样式变量供 CSS v-bind 使用
const vipBg = config.colors.vipBg
const purpleTextGradient = config.colors.purpleTextGradient
const pinkGradient = config.colors.pinkGradient

// 获取订阅商品项的背景样式
function getSubscribeItemStyle(item: any) {
  return {
    background: getIsImageBackground(item)
      ? `url(${config.colors.memberSubscribeBg}) no-repeat`
      : config.colors.subscribeActiveBg,
    backgroundSize: '100% 100%',
  }
}
</script>

<template>
  <BasicPopup :value="value" @input="value => $emit('input', value)">
    <div class="popup-recharge w-full px-5 pt-20px pb-30px bg-n-bg-card rounded-t-lg relative">
      <h3 class="text-18px text-center text-n-text-1">
        {{ config.texts.payTemplateTitle }}
      </h3>
      <p class="text-14px text-n-text-2 text-center">
        {{ config.texts.payTemplateDesc }}
      </p>
      <template v-if="showPaymentMethodSelector">
        <!-- 支付方式选择器 -->
        <PaymentMethodSelector
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="val => currentPaymentMethod = val"
        />
      </template>

      <!-- 支付 -->
      <div class="flex flex-wrap justify-between">
        <div
          v-for="(item, index) in templateProducts"
          :key="index"
          class="relative border b-rd-8px w-48% h-70px mt-4 flex flex-col justify-center items-center text-n-text-2"
          :style="{
            border: active === index ? `1px solid ${colors?.primary}` : `1px solid ${colors?.border.primary}`,
            background: active === index ? config.colors.vipBg : colors?.bg.card,
          }"
          @click="handlePayTemplate(item, index)"
        >
          <div
            v-if="item.marketingText"
            class="pos-absolute !text-white left--1px top--8px min-w-50px h-16px text-10px py-2px px-2 flex items-center"
            :style="{ background: config.colors.pinkGradient, borderRadius: '100px 100px 100px 0px' }"
          >
            {{ item.marketingText }}
          </div>
          <div>
            <span class="text-18px font-medium text-n-text-1">{{ item.coinsBuy }}+{{ item.coinsBonus }}</span> {{ config.texts.payTemplateCoins }}
          </div>
          <div class="mt-2px">
            $ {{ item.price }}
          </div>
        </div>
      </div>

      <!-- 订阅 - 列表样式（只有订阅商品时） -->
      <div v-if="!userStore.$state.subscribe && subscribes?.length && hasOnlySubscribes" class="subscribe-list-container mt-4">
        <div class="subscribe-list">
          <div
            v-for="(item, index) in subscribes"
            :key="index"
            class="subscribe-list-item rounded-8px mb-3"
            :style="getSubscribeItemStyle(item)"
            @click="onSubscribeProducts(item)"
          >
            <SubscribeItemContent :item="item" :get-unit-price="getUnitPrice" :is-zero="isZero" />
          </div>
        </div>
      </div>

      <!-- 订阅 - 轮播样式（有充值商品时） -->
      <div v-else-if="!userStore.$state.subscribe && subscribes?.length" class="banner swiper mt-4">
        <div class="swiper-wrapper ">
          <div
            v-for="(item, index) in subscribes"
            :key="index"
            class="swiper-slide swiper-item rounded-8px"
            :style="getSubscribeItemStyle(item)"
            @click="onSubscribeProducts(item)"
          >
            <SubscribeItemContent :item="item" :get-unit-price="getUnitPrice" :is-zero="isZero" />
          </div>
        </div>
        <div class="swiper-pagination flex items-center justify-center" />
      </div>

      <div v-if="!loaded" class="h-260px flex items-center justify-center flex-col">
        <BasicSpinner class="mx-auto" />
        <div class="mt-2 font-bold">
          {{ config.texts.loading }}
        </div>
      </div>
    </div>
  </BasicPopup>
</template>

<style lang="scss" scoped>
.pay-template-info:hover {
  border: 1px solid var(--primary-color);
  background-color: v-bind(vipBg);
}
.swiper-item{
  height: 115px;
}
@media screen and (min-width: 380px) {
  .swiper-item{
    min-height: 91px;
  }
}
.cycleBg {
  background: v-bind(purpleTextGradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

/* 订阅列表样式 */
.subscribe-list-container {
  max-height: 360px;
  overflow: hidden;
  position: relative;
}

.subscribe-list {
  max-height: 360px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
  /* 防止 iOS 长按时出现弹性滚动和横向移动 */
  overscroll-behavior: contain;
  touch-action: pan-y;
}

.subscribe-list-item {
  min-height: 91px;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }

  &:last-child {
    margin-bottom: 0 !important;
  }
}
</style>

<style lang="scss">
.popup-recharge {
  .swiper-pagination-bullet {
    width: 6px;
    height: 6px;
    background: #ffffff;
    border-radius: 3px;
    opacity: 0.5;
  }

  .swiper-pagination-bullet-active {
    width: 20px;
    height: 6px;
    background: #ffffff;
    border-radius: 3px;
    opacity: 1;
  }

  .popular-tag {
    position: absolute;
    top: 0;
    left: 0;
    min-width: 56px;
    height: 18px;
    background: v-bind(pinkGradient);
    border-radius: 8px 0px 12px 0px;
    font-weight: 400;
    font-size: 10px;
    color: white !important;
    text-align: center;
  }
}
</style>
